import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createSla,
  removeSla,
  slaConfigurationQuery,
  slaOperationsQuery,
  slaShellQuery,
  slaSubcategoriesQuery,
  slaTableQuery,
  updateSla,
  type SlaFields,
  type SlaShell,
} from '../../api/settingsEaseedeskSla'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → SLA configuration.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/sla/
 * index.php` + `edit_form.php`, si coloana cu coloana fata de lista
 * DataTables din portal.
 *
 * O REGULA e un rand cu sapte dimensiuni - serviciu, client, tip, categorie,
 * subcategorie, operatiune, prioritate - si un numar de MINUTE. "Any" pe o
 * dimensiune inseamna `0`, adica "oricare".
 *
 * SERVICIUL SI CLIENTUL POT LIPSI din baza: coloanele au venit cu o migrare
 * care poate sa nu fi ajuns. `has_service` din capul de api spune daca au
 * ajuns, si pana atunci cele doua campuri nu se arata - ca in macheta veche.
 *
 * Ecranul asta SCRIE reguli. Cat SLA are un tichet anume se afla din alta
 * parte (`Sla_configuration::strictestFor()`), pe drumul scrierilor de pe
 * tichet, si nu se atinge de aici.
 */

const TABLE_ID = 'sla-configuration'

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** „oricare" se arata ca „Any", nu ca o celula goala: goala pare o data lipsa. */
function anyOr(value: unknown): string {
  const text = String(value ?? '').trim()
  return text === '' ? '<span class="text-muted">Any</span>' : esc(text)
}

export function slaColumns(hasService: boolean): TableColumn[] {
  const columns: TableColumn[] = [
    {
      index: 0,
      title: 'Service',
      orderable: true,
      visible: hasService,
      className: 'a-left',
      html: (row) => anyOr(row.ci_name),
    },
    {
      index: 1,
      title: 'Customer',
      orderable: true,
      visible: hasService,
      className: 'a-left',
      html: (row) => anyOr(row.company_name),
    },
    {
      index: 2,
      title: 'Ticket type',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        String(row.type_name ?? '') === ''
          ? anyOr('')
          : `<span class="label" style="background-color: ${esc(String(row.color_identifier ?? ''))}">${esc(
              String(row.type_name),
            )}</span>`,
    },
    {
      index: 3,
      title: 'Category',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => anyOr(row.category_name),
    },
    {
      index: 4,
      title: 'Subcategory',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => anyOr(row.subcategory_name),
    },
    {
      index: 5,
      title: 'Operation',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => anyOr(row.operation_name),
    },
    {
      index: 6,
      title: 'Priority',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        String(row.priority_name ?? '') === ''
          ? anyOr('')
          : `<span class="label" style="background-color: ${esc(String(row.priority_color ?? ''))}">${esc(
              String(row.priority_name),
            )}</span>`,
    },
    {
      index: 7,
      title: 'SLA (minutes)',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<span class="label label-primary">${Number(row.sla ?? 0)}</span>`,
    },
    {
      index: 8,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) =>
        '<div class="btn-group" role="group" aria-label="controls">' +
        `<button type="button" data-sla="${Number(row.ID)}" class="btn btn-sm btn-default edit-sla">` +
        '<i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>' +
        `<button type="button" data-sla="${Number(row.ID)}" class="btn btn-sm btn-default remove-sla">` +
        '<i class="fas fa-times" aria-hidden="true"></i> Remove</button>' +
        '</div>',
    },
  ]

  return columns
}

export const ORDER_NAMES = [
  'ci_name',
  'company_name',
  'type_name',
  'category_name',
  'subcategory_name',
  'operation_name',
  'priority_name',
  'sla',
  'sla',
]

export function SlaSettingsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(slaShellQuery())
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 7, dir: 'asc' },
    search: '',
  })
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState(0)
  const hasService = shell.data?.has_service ?? false
  const columns = useMemo(() => slaColumns(hasService), [hasService])

  const table = useQuery({
    ...slaTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'sla',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'sla_settings'] })
  }

  const remove = useMutation({
    mutationFn: (sla: number) => removeSla(sla),
    onSuccess: () => {
      toast('SLA configuration deleted', 'Success')
      redraw()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const edit = target.closest('.edit-sla')
    if (edit instanceof HTMLElement) {
      event.preventDefault()
      setEdited(Number(edit.dataset.sla ?? 0))
      return
    }

    const kill = target.closest('.remove-sla')
    if (kill instanceof HTMLElement) {
      event.preventDefault()
      if (!window.confirm('Are you sure?\nYou will not be able to recover this SLA configuration!')) return
      remove.mutate(Number(kill.dataset.sla ?? 0))
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> New configuration
      </button>
      <h4>
        <strong>SLA</strong> configuration
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Edit <strong>SLA</strong>
      </label>
      <hr />

      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="An SLA rule says how many minutes a ticket has, for a given service, customer, type, category, subcategory, operation and priority."
      />

      <SlaModal open={creating} shell={shell.data} onClose={() => setCreating(false)} onSaved={redraw} />
      <SlaModal
        sla={edited}
        open={edited > 0}
        shell={shell.data}
        onClose={() => setEdited(0)}
        onSaved={redraw}
      />
    </div>
  )
}

const EMPTY: SlaFields = {
  ci_id: 0,
  company_id: 0,
  type_id: 0,
  category_id: 0,
  subcategory_id: 0,
  operation_id: 0,
  priority_id: 0,
  sla_duration: 0,
}

/**
 * `newSLAModal` si `editSLAModal`: acelasi formular, aceleasi liste.
 *
 * Categoria, subcategoria si operatiunea sunt inlantuite: cand se schimba una,
 * cele de sub ea se golesc si se cer din nou - la fel ca in portal, unde
 * `getSubcategoriesData` si `getOperationsData` erau chemate la fiecare
 * schimbare.
 */
export function SlaModal({
  sla = 0,
  open,
  shell,
  onClose,
  onSaved,
}: {
  sla?: number
  open: boolean
  shell: SlaShell
  onClose(): void
  onSaved(): void
}) {
  const editing = sla > 0
  const details = useQuery(slaConfigurationQuery(open && editing ? sla : 0))
  const [fields, setFields] = useState<SlaFields>(EMPTY)

  useEffect(() => {
    if (!open) return
    if (!editing) {
      setFields(EMPTY)
      return
    }
    if (!details.data) return
    const c = details.data.configuration
    setFields({
      ci_id: c.ci_id,
      company_id: c.company_id,
      type_id: c.type_id,
      category_id: c.category_id,
      subcategory_id: c.subcategory_id,
      operation_id: c.operation_id,
      priority_id: c.priority_id,
      sla_duration: c.sla,
    })
  }, [open, editing, details.data])

  const subcategories = useQuery(slaSubcategoriesQuery(fields.category_id))
  const operations = useQuery(slaOperationsQuery(fields.subcategory_id))

  const save = useMutation({
    mutationFn: () => (editing ? updateSla(sla, fields) : createSla(fields)),
    onSuccess: () => {
      toast(editing ? 'SLA configuration updated.' : 'New SLA configuration created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editSLAModal' : 'newSLAModal'}
      labelledBy={editing ? 'editSLAModalLabel' : 'newSLAModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit SLA configuration' : 'New SLA configuration'}
    >
      {editing && !details.data ? (
        <Spinner />
      ) : (
        <form
          id={editing ? 'edit-sla-form' : 'new-sla-form'}
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (fields.sla_duration < 1) return
            save.mutate()
          }}
        >
          {shell.has_service && (
            <>
              <div className="form-group">
                <label htmlFor="sla-service" className="control-label">
                  Service
                </label>
                <select
                  id="sla-service"
                  className="form-control input-sm"
                  name="ci_id"
                  value={fields.ci_id}
                  onChange={(e) => setFields((f) => ({ ...f, ci_id: Number(e.target.value) }))}
                >
                  <option value={0}>Any</option>
                  {shell.services.map((service) => (
                    <option key={service.ID} value={service.ID}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="sla-customer" className="control-label">
                  Customer
                </label>
                <select
                  id="sla-customer"
                  className="form-control input-sm"
                  name="company_id"
                  value={fields.company_id}
                  onChange={(e) => setFields((f) => ({ ...f, company_id: Number(e.target.value) }))}
                >
                  <option value={0}>Any</option>
                  {shell.companies.map((company) => (
                    <option key={company.ID} value={company.ID}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="sla-type" className="control-label">
              Ticket type
            </label>
            <select
              id="sla-type"
              className="form-control input-sm"
              name="type_id"
              value={fields.type_id}
              onChange={(e) => setFields((f) => ({ ...f, type_id: Number(e.target.value) }))}
            >
              <option value={0}>Any</option>
              {shell.types.map((type) => (
                <option key={type.ID} value={type.ID}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sla-category" className="control-label">
              Category
            </label>
            <select
              id="sla-category"
              className="form-control input-sm"
              name="category_id"
              value={fields.category_id}
              onChange={(e) =>
                setFields((f) => ({
                  ...f,
                  category_id: Number(e.target.value),
                  subcategory_id: 0,
                  operation_id: 0,
                }))
              }
            >
              <option value={0}>Any</option>
              {shell.categories.map((category) => (
                <option key={category.ID} value={category.ID}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sla-subcategory" className="control-label">
              Subcategory
            </label>
            <select
              id="sla-subcategory"
              className="form-control input-sm"
              name="subcategory_id"
              value={fields.subcategory_id}
              disabled={fields.category_id < 1}
              onChange={(e) =>
                setFields((f) => ({ ...f, subcategory_id: Number(e.target.value), operation_id: 0 }))
              }
            >
              <option value={0}>Any</option>
              {(subcategories.data?.subcategories ?? []).map((sub) => (
                <option key={sub.ID} value={sub.ID}>
                  {sub.subcategory_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sla-operation" className="control-label">
              Operation
            </label>
            <select
              id="sla-operation"
              className="form-control input-sm"
              name="operation_id"
              value={fields.operation_id}
              disabled={fields.subcategory_id < 1}
              onChange={(e) => setFields((f) => ({ ...f, operation_id: Number(e.target.value) }))}
            >
              <option value={0}>Any</option>
              {(operations.data?.operations ?? []).map((operation) => (
                <option key={operation.ID} value={operation.ID}>
                  {operation.operation_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sla-priority" className="control-label">
              Priority
            </label>
            <select
              id="sla-priority"
              className="form-control input-sm"
              name="priority_id"
              value={fields.priority_id}
              onChange={(e) => setFields((f) => ({ ...f, priority_id: Number(e.target.value) }))}
            >
              <option value={0}>Any</option>
              {shell.priorities.map((priority) => (
                <option key={priority.ID} value={priority.ID}>
                  {priority.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sla-duration" className="control-label">
              SLA (minutes) <span className="required">*</span>
            </label>
            <input
              id="sla-duration"
              type="number"
              className="form-control input-sm"
              required
              min={0}
              name="sla_duration"
              value={fields.sla_duration}
              onChange={(e) => setFields((f) => ({ ...f, sla_duration: Number(e.target.value) }))}
            />
          </div>

          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button
              type="submit"
              className={editing ? 'btn btn-primary submit-update-sla' : 'btn btn-primary submit-new-sla'}
              disabled={fields.sla_duration < 1 || save.isPending}
            >
              {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
